import {
  AnalogJoint,
  AnalogLink,
  AnalogPanel,
  Bearing,
  BearingEnvelope,
  CalculatedLine,
  CeilingPlane,
  CeilingTray,
  Chase,
  ComponentDesign,
  EndCondition,
  FeaturePatternTrussContainer,
  Gusset,
  Hanger,
  JackGirderConnection,
  LayoutFeatureArea,
  LayoutModel,
  Load,
  LoadCase,
  ManualLoadSettings,
  Member,
  MovingLoadSettings,
  ParapetManualLoadSettings,
  PlatePair,
  ProductionComponentInstance,
  ProfileJoint,
  RoofContainer,
  RoofPlane,
  Run,
  SnappingUnderlay,
  Splice,
  TrussEnvelope,
  TrussGroup,
  WebJoint,
  WebJointSegment,
  WebSegment,
  WorkLine,
  Zone,
} from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

export type SelectedItemIdentifierOfType<Type extends SelectableItemType> = {
  type: Type;
  guid: string;
};

export type SelectedItemIdentifier = SelectedItemIdentifierOfType<SelectableItemType>;

/**
 * Sorts a selectable item type into one of four categories: context, hierarchy, design, or layout. This is used in
 *   various places to determine an item should be treated. See also `isConsideredLayoutItem` and
 *   `isConsideredComponentDesignItem` for an older version of this function.
 */
export function categorizeSelectableItemType(type: SelectableItemType): 'context' | 'hierarchy' | 'design' | 'layout' {
  switch (type) {
    case 'project':
      return 'context';

    case 'beam':
    case 'ceilingPlane':
    case 'ceilingTray':
    case 'featureArea':
    case 'featurePatternContainer':
    case 'snappingUnderlay':
    case 'wall':
    case 'workLine':
    case 'roofContainer':
    case 'roofPlane':
    case 'trussEnvelope':
      return 'layout';

    case 'analogJoint':
    case 'analogLink':
    case 'bearing':
    case 'brace':
    case 'chase':
    case 'componentDesign':
    case 'endCondition':
    case 'gusset':
    case 'hanger':
    case 'jackGirderConnection':
    case 'load':
    case 'loadCase':
    case 'manualLoad':
    case 'member':
    case 'movingLoad':
    case 'panel':
    case 'parapetLoad':
    case 'platePair':
    case 'productionComponentInstance':
    case 'profileJoint':
    case 'referenceLine':
    case 'run':
    case 'splice':
    case 'webJoint':
    case 'webJointSegment':
    case 'webSegment':
    case 'zone':
      return 'design';

    case 'building':
    case 'level':
    case 'trussGroup':
      return 'hierarchy';

    default:
      assertExhaustiveSwitch(type);
  }
}

export function isConsideredLayoutItem(type: SelectableItemType) {
  return categorizeSelectableItemType(type) !== 'design';
}

/**
 * NOTE: This does not always indicate that an active component design exists. For example, when inserting a profile and
 * the component design fails to generate, there will be no active component design.
 */
export function isConsideredDesignItem(type: SelectableItemType) {
  return !isConsideredLayoutItem(type);
}

export function typeToUserVisibleString(type: SelectableItemType) {
  switch (type) {
    case 'analogJoint':
      return 'analog joint';
    case 'analogLink':
      return 'analog link';
    case 'beam':
      return 'beam';
    case 'bearing':
      return 'bearing';
    case 'brace':
      return 'brace';
    case 'building':
      return 'building';
    case 'ceilingPlane':
      return 'ceiling plane';
    case 'ceilingTray':
      return 'ceiling tray';
    case 'chase':
      return 'chase';
    case 'componentDesign':
      return 'component design';
    case 'endCondition':
      return 'end condition';
    case 'featureArea':
      return 'feature area';
    case 'featurePatternContainer':
      return 'feature pattern container';
    case 'gusset':
      return 'gusset';
    case 'hanger':
      return 'hanger';
    case 'jackGirderConnection':
      return 'jack girder connection';
    case 'workLine':
      return 'work line';
    case 'level':
      return 'level';
    case 'load':
      return 'load';
    case 'loadCase':
      return 'load case';
    case 'manualLoad':
      return 'manual load';
    case 'member':
      return 'member';
    case 'movingLoad':
      return 'moving load';
    case 'panel':
      return 'panel';
    case 'parapetLoad':
      return 'parapet load';
    case 'platePair':
      return 'plate pair';
    case 'profileJoint':
      return 'profile joint';
    case 'productionComponentInstance':
      return 'production component instance';
    case 'project':
      return 'project';
    case 'referenceLine':
      return 'reference line';
    case 'roofContainer':
      return 'roof container';
    case 'roofPlane':
      return 'roof plane';
    case 'run':
      return 'run';
    case 'snappingUnderlay':
      return 'snapping underlay';
    case 'splice':
      return 'splice';
    case 'trussEnvelope':
      return 'truss envelope';
    case 'trussGroup':
      return 'truss group';
    case 'wall':
      return 'wall';
    case 'webJoint':
      return 'web joint';
    case 'webJointSegment':
      return 'web joint segment';
    case 'webSegment':
      return 'web segment';
    case 'zone':
      return 'zone';
    default:
      assertExhaustiveSwitch(type);
  }
}

export function typeToPluralUserVisibleString(type: SelectableItemType) {
  switch (type) {
    case 'analogJoint':
      return 'analog joints';
    case 'analogLink':
      return 'analog links';
    case 'beam':
      return 'beams';
    case 'bearing':
      return 'bearings';
    case 'brace':
      return 'braces';
    case 'building':
      return 'buildings';
    case 'ceilingPlane':
      return 'ceiling planes';
    case 'ceilingTray':
      return 'ceiling trays';
    case 'chase':
      return 'chases';
    case 'componentDesign':
      return 'component designs';
    case 'endCondition':
      return 'end conditions';
    case 'featureArea':
      return 'feature areas';
    case 'featurePatternContainer':
      return 'feature pattern containers';
    case 'gusset':
      return 'gussets';
    case 'hanger':
      return 'hangers';
    case 'jackGirderConnection':
      return 'jack girder connections';
    case 'workLine':
      return 'work lines';
    case 'level':
      return 'levels';
    case 'load':
      return 'loads';
    case 'loadCase':
      return 'load cases';
    case 'manualLoad':
      return 'manual loads';
    case 'member':
      return 'members';
    case 'movingLoad':
      return 'moving loads';
    case 'panel':
      return 'panels';
    case 'parapetLoad':
      return 'parapet loads';
    case 'platePair':
      return 'plate pairs';
    case 'profileJoint':
      return 'profile joints';
    case 'productionComponentInstance':
      return 'production component instances';
    case 'project':
      return 'projects';
    case 'referenceLine':
      return 'reference lines';
    case 'roofContainer':
      return 'roof containers';
    case 'roofPlane':
      return 'roof planes';
    case 'run':
      return 'runs';
    case 'snappingUnderlay':
      return 'snapping underlays';
    case 'splice':
      return 'splices';
    case 'trussEnvelope':
      return 'truss envelopes';
    case 'trussGroup':
      return 'truss groups';
    case 'wall':
      return 'walls';
    case 'webJoint':
      return 'web joints';
    case 'webJointSegment':
      return 'web joint segments';
    case 'webSegment':
      return 'web segments';
    case 'zone':
      return 'zones';
    default:
      assertExhaustiveSwitch(type);
  }
}

export interface SelectableItemMap {
  analogJoint: AnalogJoint;
  analogLink: AnalogLink;
  beam: BearingEnvelope;
  bearing: Bearing;
  brace: Member;
  building: LayoutModel;
  ceilingTray: CeilingTray;
  ceilingPlane: CeilingPlane;
  chase: Chase;
  componentDesign: ComponentDesign;
  endCondition: EndCondition;
  featureArea: LayoutFeatureArea;
  featurePatternContainer: FeaturePatternTrussContainer;
  gusset: Gusset;
  hanger: Hanger;
  jackGirderConnection: JackGirderConnection;
  workLine: WorkLine;
  level: LayoutModel;
  load: Load;
  loadCase: LoadCase;
  manualLoad: ManualLoadSettings;
  member: Member;
  movingLoad: MovingLoadSettings;
  panel: AnalogPanel;
  parapetLoad: ParapetManualLoadSettings;
  platePair: PlatePair;
  profileJoint: ProfileJoint;
  productionComponentInstance: ProductionComponentInstance;
  project: LayoutModel;
  referenceLine: CalculatedLine;
  roofContainer: RoofContainer;
  roofPlane: RoofPlane;
  run: Run;
  snappingUnderlay: SnappingUnderlay;
  splice: Splice;
  trussEnvelope: TrussEnvelope;
  trussGroup: TrussGroup;
  wall: BearingEnvelope;
  webJoint: WebJoint;
  webJointSegment: WebJointSegment;
  webSegment: WebSegment;
  zone: Zone;
}

export type SelectableItemEntry = {
  [T in keyof SelectableItemMap]: { readonly type: T; readonly item: SelectableItemMap[T] };
}[keyof SelectableItemMap];

export type SelectableItemType = keyof SelectableItemMap;
export type SelectableItem = SelectableItemMap[SelectableItemType];

export type SelectableEntryOfType<T extends SelectableItemType> = { type: T; item: SelectableItemMap[T] };
export type SelectableEntry<T extends SelectableItem> = { type: TypeOfSelectableItem<T>; item: T };

export type SelectableItemOfType<T extends SelectableItemType> = SelectableItemMap[T];
export type TypeOfSelectableItem<T extends SelectableItem> = Extract<SelectableItemEntry, { item: T }>['type'];

export function createSelectedItemEntry<T extends SelectableItemType>(
  type: T,
  item: SelectableItemOfType<T> | null | undefined,
): SelectableEntryOfType<T> | null {
  return item ? { type, item } : null;
}

export function getEntriesOfType<T extends SelectableItemType>(
  entries: SelectableItemEntry[],
  type: T,
): SelectableEntryOfType<T>[] {
  return entries.filter((entry) => entry.type === type) as SelectableEntryOfType<T>[];
}

export function isIdentifier(thing: SelectedItemIdentifier | SelectableItemEntry): thing is SelectedItemIdentifier {
  return !isEntry(thing);
}

export function isEntry(thing: SelectedItemIdentifier | SelectableItemEntry): thing is SelectableItemEntry {
  return 'item' in thing;
}

// The following two statements are a type-safe way of constructing a string[] of the SelectableItemEntry types. Feel
//   free to change it if you come up with a better way

const selectableItemTypes: Record<SelectableItemType, boolean> = {
  analogJoint: true,
  analogLink: true,
  beam: true,
  bearing: true,
  brace: true,
  building: true,
  ceilingPlane: true,
  ceilingTray: true,
  chase: true,
  componentDesign: true,
  endCondition: true,
  featureArea: true,
  featurePatternContainer: true,
  gusset: true,
  hanger: true,
  jackGirderConnection: true,
  workLine: true,
  level: true,
  load: true,
  loadCase: true,
  manualLoad: true,
  member: true,
  movingLoad: true,
  panel: true,
  parapetLoad: true,
  platePair: true,
  profileJoint: true,
  productionComponentInstance: true,
  project: true,
  referenceLine: true,
  roofContainer: true,
  roofPlane: true,
  run: true,
  snappingUnderlay: true,
  splice: true,
  trussEnvelope: true,
  trussGroup: true,
  wall: true,
  webJoint: true,
  webJointSegment: true,
  webSegment: true,
  zone: true,
};

export const ALL_SELECTABLE_ITEM_TYPES = Object.keys(selectableItemTypes) as SelectableItemType[];

export function isSelectableItemType(type: string): type is SelectableItemType {
  return type in selectableItemTypes;
}

export const LEGACY_BEARING_ENVELOPE_URL_TYPE = 'bearingEnvelope';

export function getBearingEnvelopeSelectableType(envelope: BearingEnvelope): 'beam' | 'wall' {
  switch (envelope.bearingType) {
    case 'Beam':
      return 'beam';

    case 'Wall':
      return 'wall';

    default:
      assertExhaustiveSwitch(envelope.bearingType);
  }
}

export const BEARING_ENVELOPE_SELECTABLE_TYPES = ['beam', 'wall'] as const;

export function isBearingEnvelopeSelectableType(type: SelectableItemType): type is 'beam' | 'wall' {
  return type === 'beam' || type === 'wall';
}
